import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import type { User } from "@/schemaTypes";
import { ChannelLeadersDocument } from "../graphql/channelLeaders.generated";
import { useEventRefetch } from "../events";
import { noCacheHeaders } from "@/utils/headers";
import { useHomepageSsr } from "@/providers/HomepageSsrProvider";

type Props = {
  channelId?: string;
  skip?: boolean;
};

export const useGetChannelLeaders = ({ channelId, skip }: Props) => {
  const { channelDetail, isSsrChannelDetail } = useHomepageSsr();
  const hasMatchingServerResult = Boolean(
    channelId &&
      isSsrChannelDetail &&
      channelDetail?.channel?.id === channelId &&
      channelDetail.leadersResolved
  );
  const shouldSkip = skip || !channelId || hasMatchingServerResult;

  const { data, loading, error, refetch } = useQuery(ChannelLeadersDocument, {
    variables: { channelId: channelId! },
    skip: shouldSkip,
    fetchPolicy: "no-cache",
    context: { headers: noCacheHeaders },
  });

  useEventRefetch("channelUpdated", refetch);

  const leaders: User[] = useMemo(() => {
    return (
      (data?.channelLeaders?.filter(Boolean) as User[]) ??
      (hasMatchingServerResult
        ? (channelDetail?.leaders.filter(Boolean) as User[])
        : [])
    );
  }, [channelDetail?.leaders, data, hasMatchingServerResult]);

  return {
    loading: hasMatchingServerResult ? false : loading,
    leaders,
    error,
  };
};
